import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { partners as mockPartners, additionalPartners } from '../data/mock';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { getText, staticTexts } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PartnersPage = () => {
  const { t, language } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const txt = staticTexts.partners;

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/partners`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setPartners(data);
        } else {
          setPartners(mockPartners);
        }
      })
      .catch(() => {
        setPartners(mockPartners);
      })
      .finally(() => setLoading(false));
  }, []);

  // Helper to get partner description based on language
  const getPartnerDescription = (partner) => {
    if (language === 'ar' && partner.description_ar) return partner.description_ar;
    if (language === 'fr' && (partner.description_fr || partner.description?.fr)) {
      return partner.description_fr || partner.description.fr;
    }
    return partner.description_en || partner.description?.en || partner.description?.fr || '';
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
            {getText(language, txt.ourNetwork.en, txt.ourNetwork.fr, txt.ourNetwork.ar)}
          </p>
          <h1 className="text-5xl md:text-6xl font-light text-slate-800">
            {getText(language, txt.title.en, txt.title.fr, txt.title.ar)}
          </h1>
        </div>
      </section>

      {/* Main Partners Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partners.map((partner) => (
              <Link
                key={partner.id}
                to={`/partner/${partner.id}`}
                className="group bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
              >
                <div className="h-16 flex items-center justify-center mb-6">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-full w-auto"
                  />
                </div>
                <h3 className="text-lg font-medium text-slate-800 mb-1">
                  {partner.name}
                </h3>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">{partner.country}</p>
                <p className="text-sm text-slate-500 line-clamp-2 mb-4">
                  {getPartnerDescription(partner)}
                </p>
                <span className="text-sm text-slate-800 flex items-center gap-1 group-hover:gap-2 transition-all">
                  {getText(language, txt.learnMore.en, txt.learnMore.fr, txt.learnMore.ar)}
                  <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Partners */}
      <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-8 text-center">
            {getText(language, txt.alsoRepresentative.en, txt.alsoRepresentative.fr, txt.alsoRepresentative.ar)}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {additionalPartners.map((partner, index) => (
              <a
                key={index}
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl p-6 flex flex-col items-center justify-center hover:shadow-md transition-all group"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-12 w-auto mb-3 grayscale group-hover:grayscale-0 transition-all"
                />
                <span className="text-xs text-slate-500">{partner.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-800">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-light text-white mb-4">
            {getText(language, txt.becomePartner.en, txt.becomePartner.fr, txt.becomePartner.ar)}
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            {getText(language, txt.joinNetwork.en, txt.joinNetwork.fr, txt.joinNetwork.ar)}
          </p>
          <Link to="/become-partner">
            <Button className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-sm rounded-full">
              {t('nav.becomePartner')}
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default PartnersPage;
